import React, { useState, useEffect, useCallback } from "react";
import { Bell, Search, X, Calendar, AlertTriangle, AlertCircle, Info, CheckCircle2, Eye, Download } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import baseapi from "../../services/baseapi";
import { type LocationIds } from "../../components/LocationSelectorSection";
import AgentExportModal from "../Agent/AgentExportModal";

interface EventListSidebarProps {
    isOpen: boolean;
    onClose: () => void;
    initialStatus: "all" | "open" | "closed";
    locationIds: LocationIds;
    level: any;
    selectedDistrictId?: number | null;
    selectedStationId?: number | null;
}

const getSeverityConfig = (severity: string) => {
    const s = severity?.toLowerCase() || '';
    if (s === 'critical') return { color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-100', icon: AlertTriangle, dot: 'bg-red-500' };
    if (s === 'high' || s === 'error') return { color: 'text-[#422568]', bg: 'bg-[#422568]/10', border: 'border-[#422568]/20', icon: AlertCircle, dot: 'bg-[#422568]' };
    if (s === 'medium' || s === 'warning') return { color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-100', icon: AlertCircle, dot: 'bg-yellow-500' };
    if (s === 'low' || s === 'info') return { color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-100', icon: Info, dot: 'bg-blue-500' };
    if (s === 'success') return { color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-100', icon: CheckCircle2, dot: 'bg-green-500' };
    return { color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-100', icon: Info, dot: 'bg-slate-500' };
};

const getStatusConfig = (status: string) => {
    const s = status?.toLowerCase() || '';
    if (s === 'open') return { color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-100', dot: 'bg-yellow-500' };
    if (s === 'closed') return { color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-100', dot: 'bg-green-500' };
    return { color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-100', dot: 'bg-slate-500' };
};

const formatTimestamp = (ts: string) => {
    if (!ts) return '-';
    try {
        const date = new Date(ts);
        return date.toLocaleString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: true
        });
    } catch { return ts; }
};

const formatEventType = (type: string) => {
    if (!type) return '-';
    return type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

const getSnapshotUrl = (event: any) => {
    if (event.event_type !== 'intrusion_detection' && event.event_type !== 'camera_tampering') return null;
    try {
        const parsed = typeof event.event_data === 'string' ? JSON.parse(event.event_data) : event.event_data;
        if (Array.isArray(parsed) && parsed.length > 0) {
            return parsed[0].data?.snapshot_url || parsed[0].snapshot_url || null;
        }
        if (parsed && typeof parsed === 'object') {
            return parsed.data?.snapshot_url || parsed.snapshot_url || null;
        }
    } catch (e) {
        console.error("Failed to parse event_data for snapshot_url:", e);
    }
    return null;
};

export const EventListSidebar: React.FC<EventListSidebarProps> = ({
    isOpen,
    onClose,
    initialStatus,
    locationIds,
    level,
    selectedDistrictId,
    selectedStationId
}) => {
    const [eventListStatus, setEventListStatus] = useState<"all" | "open" | "closed">(initialStatus);
    const [eventListLoading, setEventListLoading] = useState(false);
    const [eventList, setEventList] = useState<any[]>([]);
    const [eventListSearch, setEventListSearch] = useState("");
    const [eventListPage, setEventListPage] = useState(1);
    const [eventListLimit] = useState(10);
    const [eventListTotal, setEventListTotal] = useState(0);
    const [selectedSnapshot, setSelectedSnapshot] = useState<string | null>(null);
    const [showExportModal, setShowExportModal] = useState(false);

    const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
    const [prevInitialStatus, setPrevInitialStatus] = useState(initialStatus);

    if (isOpen !== prevIsOpen || initialStatus !== prevInitialStatus) {
        setPrevIsOpen(isOpen);
        setPrevInitialStatus(initialStatus);
        if (isOpen) {
            setEventListStatus(initialStatus);
            setEventListPage(1);
            setEventListSearch("");
        }
    }

    const fetchEventList = useCallback(async (page = 1, search = "") => {
        setEventListLoading(true);
        try {
            const queryObj: any = {};
            if (eventListStatus !== "all") {
                queryObj.event_status = eventListStatus;
            }

            if (locationIds.regionId) queryObj.region_id = locationIds.regionId;
            if (locationIds.stateId) queryObj.state_id = locationIds.stateId;
            if (locationIds.districtId) queryObj.district_id = locationIds.districtId;
            if (locationIds.townId) queryObj.town_id = locationIds.townId;

            // Apply level and custom selected districts/stations
            if (selectedStationId) {
                queryObj.client_id = selectedStationId;
            } else if (locationIds.siteId && locationIds.siteId !== "all") {
                queryObj.client_id = locationIds.siteId;
            } else if (level?.id) {
                queryObj.client_id = Number(level.id);
            }

            if (selectedDistrictId) {
                queryObj.district_id = selectedDistrictId;
            }

            if (search.trim()) {
                queryObj.$or = [
                    { message: { "$ilike": `%${search}%` } },
                    { description: { "$ilike": `%${search}%` } },
                    { event_type: { "$ilike": `%${search}%` } },
                    { asset_hostname: { "$ilike": `%${search}%` } },
                    { asset_ip: { "$ilike": `%${search}%` } },
                    { agent_name: { "$ilike": `%${search}%` } }
                ];
            }

            const response = await baseapi.get("/events", {
                params: {
                    query: JSON.stringify(queryObj),
                    skip: (page - 1) * eventListLimit,
                    limit: eventListLimit,
                    sort_by: "created",
                    sort_order: "desc"
                }
            });

            if (response.data?.status) {
                setEventList(response.data.data || []);
                setEventListTotal(response.data.total || 0);
            } else {
                setEventList([]);
                setEventListTotal(0);
            }
        } catch (error) {
            console.error("Failed to fetch event list:", error);
            setEventList([]);
            setEventListTotal(0);
        } finally {
            setEventListLoading(false);
        }
    }, [eventListStatus, locationIds, level, selectedDistrictId, selectedStationId, eventListLimit]);

    useEffect(() => {
        if (isOpen) {
            fetchEventList(eventListPage, eventListSearch);
        }
    }, [isOpen, eventListStatus, eventListPage, eventListSearch, fetchEventList]);

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[100]"
                    />

                    {/* Side panel */}
                    <motion.div
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{ type: "spring", damping: 25, stiffness: 200 }}
                        className="fixed right-0 top-0 h-full w-[500px] bg-white border-l border-slate-200 z-[101] flex flex-col shadow-2xl"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50/50 shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-xl bg-[#422568]/10 text-[#422568]">
                                    <Bell className="w-5 h-5" />
                                </div>
                                <div>
                                    <h2 className="text-sm font-black text-slate-900 uppercase tracking-widest leading-none">
                                        {eventListStatus === "all" ? "Total" : eventListStatus === "open" ? "Open" : "Closed"} Events
                                    </h2>
                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tight mt-1">
                                        Active and historical alerts audit trail
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setShowExportModal(true)}
                                    className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl px-3 py-1.5 text-[10px] text-slate-800 font-black uppercase hover:bg-slate-100 transition-colors shadow-sm"
                                >
                                    <Download className="w-3.5 h-3.5 text-cyan-500" />
                                    Export
                                </button>
                                <button
                                    onClick={onClose}
                                    className="p-2 rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-900 transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        {/* Content Area */}
                        <div className="flex-1 min-h-0 flex flex-col p-4">
                            {/* Status Tabs Selector inside sidebar */}
                            <div className="flex border-b border-slate-100 mb-4 text-[10px] font-black uppercase tracking-wider gap-4 shrink-0">
                                <button
                                    onClick={() => {
                                        setEventListStatus("all");
                                        setEventListPage(1);
                                    }}
                                    className={`pb-2 border-b-2 transition-all ${eventListStatus === "all"
                                        ? "border-[#422568] text-[#422568]"
                                        : "border-transparent text-slate-400 hover:text-slate-600"
                                        }`}
                                >
                                    Total
                                </button>
                                <button
                                    onClick={() => {
                                        setEventListStatus("open");
                                        setEventListPage(1);
                                    }}
                                    className={`pb-2 border-b-2 transition-all flex items-center gap-1 ${eventListStatus === "open"
                                        ? "border-amber-500 text-amber-600"
                                        : "border-transparent text-slate-400 hover:text-slate-600"
                                        }`}
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-amber-550" />
                                    Open
                                </button>
                                <button
                                    onClick={() => {
                                        setEventListStatus("closed");
                                        setEventListPage(1);
                                    }}
                                    className={`pb-2 border-b-2 transition-all flex items-center gap-1 ${eventListStatus === "closed"
                                        ? "border-emerald-500 text-emerald-600"
                                        : "border-transparent text-slate-400 hover:text-slate-600"
                                        }`}
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                    Closed
                                </button>
                            </div>

                            {/* Search Input */}
                            <div className="relative mb-4 shrink-0">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
                                <input
                                    type="text"
                                    placeholder="Search events by message, host, type..."
                                    className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-8 py-2 text-xs font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#422568]/15 focus:border-[#422568] transition-all"
                                    value={eventListSearch}
                                    onChange={(e) => {
                                        setEventListSearch(e.target.value);
                                        setEventListPage(1);
                                    }}
                                />
                                {eventListSearch && (
                                    <button
                                        onClick={() => {
                                            setEventListSearch("");
                                            setEventListPage(1);
                                        }}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5 rounded-full hover:bg-slate-200"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                )}
                            </div>

                            {/* Events List */}
                            <div className="flex-1 overflow-y-auto custom-scroll pr-1 space-y-2 mb-2">
                                {eventListLoading ? (
                                    <div className="h-full flex flex-col items-center justify-center py-20">
                                        <div className="w-8 h-8 border-4 border-[#422568]/10 border-t-[#422568] rounded-full animate-spin mb-3" />
                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest animate-pulse">
                                            Loading Events...
                                        </span>
                                    </div>
                                ) : eventList.length === 0 ? (
                                    <div className="h-full flex flex-col items-center justify-center py-20 text-slate-400 opacity-60">
                                        <Bell className="w-10 h-10 mb-2.5" />
                                        <p className="text-[10px] font-black uppercase tracking-wider">No Events Found</p>
                                    </div>
                                ) : (
                                    eventList.map((event: any) => {
                                        const sevConfig = getSeverityConfig(event.severity);
                                        const statusConfig = getStatusConfig(event.event_status);
                                        const snapshotUrl = getSnapshotUrl(event);

                                        return (
                                            <div
                                                key={event.id}
                                                className="bg-white border border-slate-200 hover:border-[#422568]/30 rounded-xl p-3 flex flex-col gap-1.5 transition-all shadow-sm hover:shadow group"
                                            >
                                                {/* Top row: Event type & Severity/Status */}
                                                <div className="flex justify-between items-start gap-2">
                                                    <div className="flex flex-col gap-0.5">
                                                        <h4 className="text-[11px] font-black text-slate-800 uppercase tracking-tight group-hover:text-[#422568] transition-colors leading-tight">
                                                            {formatEventType(event.event_type)}
                                                        </h4>
                                                        <span className="text-[8px] font-bold text-slate-400 uppercase tracking-wide flex items-center gap-1 mt-0.5">
                                                            <Calendar className="w-2.5 h-2.5" />
                                                            {formatTimestamp(`${event.created}Z`)}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-1.5 shrink-0">
                                                        <span
                                                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8px] font-black uppercase tracking-wider border shadow-sm ${sevConfig.bg} ${sevConfig.color} ${sevConfig.border}`}
                                                        >
                                                            <span className={`h-1 w-1 rounded-full ${sevConfig.dot}`} />
                                                            {event.severity}
                                                        </span>
                                                        <span
                                                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8px] font-black uppercase tracking-wider border shadow-sm ${statusConfig.bg} ${statusConfig.color} ${statusConfig.border}`}
                                                        >
                                                            {event.event_status}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Message / Description */}
                                                <p className="text-[10px] text-slate-650 leading-relaxed font-medium bg-slate-50/50 p-2 rounded-lg border border-slate-100">
                                                    {event.message || event.description || "No description provided."}
                                                </p>



                                                {/* Source Details (IP / Host / Client) */}
                                                <div className="flex items-center gap-1.5 text-[9px] text-slate-500 font-mono truncate leading-none pt-1.5 border-t border-slate-100/60">
                                                    <span className="font-bold text-slate-700 bg-slate-100 px-1 py-0.5 rounded text-[8px] font-sans">HOST</span>
                                                    <span className="text-slate-700 truncate max-w-[100px]" title={event.asset_hostname}>{event.asset_hostname || "--"}</span>

                                                    {event.client_name && (
                                                        <>
                                                            <span className="text-slate-300 shrink-0">•</span>
                                                            <span className="font-black text-[#422568] uppercase tracking-tight truncate max-w-[120px]" title={event.client_name}>{event.client_name}</span>
                                                        </>
                                                    )}
                                                    {snapshotUrl && (
                                                        <div className="ml-auto shrink-0">
                                                            <button
                                                                onClick={() => setSelectedSnapshot(snapshotUrl)}
                                                                className="flex items-center gap-1 px-2 py-0.8 bg-[#422568]/5 hover:bg-[#422568]/15 border border-[#422568]/15 hover:border-[#422568]/30 rounded-lg text-[#422568] transition-all font-black text-[9px] uppercase tracking-wider cursor-pointer"
                                                            >
                                                                <Eye className="w-3.5 h-3.5" /> Evidence
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>

                            {/* Pagination Controls */}
                            {eventListTotal > eventListLimit && (
                                <div className="shrink-0 pt-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/50 rounded-b-xl px-2 py-1.5">
                                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                                        Page {eventListPage} of {Math.ceil(eventListTotal / eventListLimit)}
                                    </span>
                                    <div className="flex gap-1.5">
                                        <button
                                            onClick={() => setEventListPage((p) => Math.max(1, p - 1))}
                                            disabled={eventListPage === 1}
                                            className="p-1 px-2 rounded bg-white hover:bg-slate-100 border border-slate-200 disabled:opacity-30 disabled:hover:bg-white text-[10px] font-black uppercase tracking-wider transition-all"
                                        >
                                            Prev
                                        </button>
                                        <button
                                            onClick={() =>
                                                setEventListPage((p) =>
                                                    Math.min(Math.ceil(eventListTotal / eventListLimit), p + 1)
                                                )
                                            }
                                            disabled={eventListPage >= Math.ceil(eventListTotal / eventListLimit)}
                                            className="p-1 px-2 rounded bg-white hover:bg-slate-100 border border-slate-200 disabled:opacity-30 disabled:hover:bg-white text-[10px] font-black uppercase tracking-wider transition-all"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </>
            )}

            {/* Snapshot Modal */}
            <AnimatePresence>
                {selectedSnapshot && (
                    <>
                        {/* Backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setSelectedSnapshot(null)}
                            className="fixed inset-0 bg-black/75 backdrop-blur-md z-[200] flex items-center justify-center p-4"
                        >
                            <motion.div
                                initial={{ scale: 0.95, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.95, opacity: 0 }}
                                onClick={(e) => e.stopPropagation()}
                                className="relative bg-white rounded-2xl overflow-hidden max-w-4xl w-full max-h-[85vh] shadow-2xl flex flex-col border border-slate-200"
                            >
                                {/* Modal Header */}
                                <div className="flex justify-between items-center px-4 py-3 border-b border-slate-100 bg-slate-50/80 shrink-0">
                                    <div className="flex items-center gap-2">
                                        <div className="p-1.5 bg-[#422568]/10 text-[#422568] rounded-lg">
                                            <Eye className="w-4 h-4" />
                                        </div>
                                        <span className="text-xs font-black text-slate-800 uppercase tracking-widest">Intrusion Evidence Snapshot</span>
                                    </div>
                                    <button
                                        onClick={() => setSelectedSnapshot(null)}
                                        className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-900 transition-colors"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>

                                {/* Modal Image Container */}
                                <div className="flex-1 overflow-auto bg-slate-950 p-2 flex items-center justify-center min-h-[300px]">
                                    <img
                                        src={selectedSnapshot}
                                        alt="Intrusion Evidence"
                                        className="max-w-full max-h-[60vh] object-contain rounded-lg border border-white/10"
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).src = '/owllogopng.png';
                                        }}
                                    />
                                </div>

                                {/* Modal Footer */}
                                <div className="px-4 py-3 bg-slate-50/80 border-t border-slate-100 text-center shrink-0">
                                    <a
                                        href={selectedSnapshot}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-[10px] font-black uppercase tracking-wider text-[#422568] hover:underline cursor-pointer"
                                    >
                                        Open Image in New Tab
                                    </a>
                                </div>
                            </motion.div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            <AgentExportModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                templateName="events"
                clientId={selectedStationId || (locationIds.siteId && locationIds.siteId !== "all" ? locationIds.siteId : level?.id)}
                filters={{
                    ...(eventListStatus !== "all" && { event_status: eventListStatus }),
                    ...(locationIds.regionId && { region_id: locationIds.regionId }),
                    ...(locationIds.stateId && { state_id: locationIds.stateId }),
                    ...(locationIds.districtId && { district_id: locationIds.districtId }),
                    ...(locationIds.townId && { town_id: locationIds.townId }),
                    ...(selectedDistrictId && { district_id: selectedDistrictId }),
                    ...(eventListSearch.trim() && {
                        search: eventListSearch.trim()
                    })
                }}
            />
        </AnimatePresence>
    );
};
